// === UK address fields (Add / Edit Company) ===
// Postcode type-ahead → lookup via the API (postcodes.io). Fills Town, County
// and lat/lng. Town and County are suggestions (parish / council district, not
// the Royal Mail post town) so both stay editable. Street is entered by the
// parent form. See docs/uk-compatibility-plan.md, Step 1.

import { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, Grid, TextField } from '@mui/material';

export interface UkAddressValue {
  readonly postcode: string;
  readonly town: string;
  readonly county: string;
  readonly lat: string;
  readonly lon: string;
}

interface LookupResult {
  readonly postcode: string;
  readonly town: string | null;
  readonly county: string | null;
  readonly nation: string | null;
  readonly lat: number | null;
  readonly lon: number | null;
}

interface Props {
  readonly apiUrl: string;
  readonly token: string;
  readonly value: UkAddressValue;
  readonly onChange: (patch: Partial<UkAddressValue>) => void;
}

export function UkAddressFields({ apiUrl, token, value, onChange }: Props) {
  const [options, setOptions] = useState<readonly string[]>([]);
  const [input, setInput] = useState(value.postcode);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [nation, setNation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the input in step when the parent resets / loads the form.
  useEffect(() => { setInput(value.postcode); }, [value.postcode]);

  const headers = { Authorization: `Bearer ${token}` };

  const handleInput = (text: string): void => {
    setInput(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.trim().length < 2) { setOptions([]); return; }
    debounceRef.current = setTimeout(() => {
      setLoadingOptions(true);
      void fetch(`${apiUrl}/api/v1/admin/postcodes/uk/autocomplete?q=${encodeURIComponent(text.trim())}`, { headers })
        .then(async (res) => (res.ok ? ((await res.json()) as { data: string[] }).data : []))
        .then(setOptions)
        .catch(() => setOptions([]))
        .finally(() => setLoadingOptions(false));
    }, 250);
  };

  const lookup = async (postcode: string): Promise<void> => {
    setError(null);
    setLookingUp(true);
    try {
      const res = await fetch(`${apiUrl}/api/v1/admin/postcodes/uk/${encodeURIComponent(postcode.trim())}`, { headers });
      const json = (await res.json()) as { data?: LookupResult; error?: string };
      if (!res.ok || !json.data) {
        setError(json.error ?? 'Postcode lookup failed');
        onChange({ postcode: postcode.trim().toUpperCase(), lat: '', lon: '' });
        setNation(null);
        return;
      }
      const d = json.data;
      setNation(d.nation);
      onChange({
        postcode: d.postcode,
        town: d.town ?? '',
        county: d.county ?? '',
        lat: d.lat !== null ? String(d.lat) : '',
        lon: d.lon !== null ? String(d.lon) : '',
      });
    } catch (e) {
      setError(`Postcode lookup failed: ${(e as Error).message}`);
    } finally {
      setLookingUp(false);
    }
  };

  return (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item xs={4}>
        <Autocomplete
          size="small"
          freeSolo
          options={options as string[]}
          filterOptions={(x) => x}
          loading={loadingOptions}
          inputValue={input}
          onInputChange={(_, v, reason) => { if (reason === 'input') handleInput(v); }}
          onChange={(_, v) => { if (typeof v === 'string' && v.trim()) { setInput(v); void lookup(v); } }}
          renderInput={(params) => (
            // @ts-expect-error: MUI Autocomplete renderInput params have optional props incompatible with exactOptionalPropertyTypes
            <TextField
              {...params}
              size="small"
              label="Postcode *"
              error={!!error}
              helperText={error ?? (nation ? `${nation} — type to search, pick to fill` : 'Type to search, pick to fill')}
              onBlur={() => {
                // Typed a full postcode without picking from the list.
                if (input.trim() && input.trim().toUpperCase() !== value.postcode) void lookup(input);
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingOptions || lookingUp ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          label="Town"
          value={value.town}
          onChange={(e) => onChange({ town: e.target.value })}
          fullWidth size="small" inputProps={{ maxLength: 150 }}
          helperText="Suggested from postcode — check and correct"
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          label="County"
          value={value.county}
          onChange={(e) => onChange({ county: e.target.value })}
          fullWidth size="small" inputProps={{ maxLength: 100 }}
        />
      </Grid>
    </Grid>
  );
}
